import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { inr, num } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LangContext";
import { BrandWordmark } from "@/components/Brand";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StatCard, Panel, Loading, Pill } from "@/components/kit";
import DealerPlayground from "@/components/DealerPlayground";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { Wallet, Banknote, Boxes, FileStack, LogOut, CircleUserRound, MapPin, Sparkles, LayoutDashboard, TrendingUp } from "lucide-react";

export default function DealerPortal() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [tab, setTab] = useState("playground");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["dealer-portal"],
    queryFn: async () => (await api.get("/dealer/portal")).data,
    retry: false,
  });

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="h-16 sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200 flex items-center justify-between px-4 sm:px-8 shadow-sm">
        <BrandWordmark />
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher />
          <div className="hidden sm:flex flex-col items-end leading-tight">
            <span className="text-sm font-semibold text-slate-800" data-testid="dealer-portal-name">{user?.name}</span>
            <span className="text-[11px] text-[#00529B] font-medium">{t("dealerPortal")}</span>
          </div>
          <div className="w-9 h-9 rounded-full adani-navy-gradient flex items-center justify-center text-white">
            <CircleUserRound size={20} />
          </div>
          <button onClick={handleLogout} data-testid="dealer-logout-btn"
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#DC2626] font-medium px-2 py-1.5 rounded-md hover:bg-red-50">
            <LogOut size={16} /> <span className="hidden sm:inline">{t("logout")}</span>
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
        {isLoading ? <Loading label={t("preparing")} /> : isError || !data ? (
          <div className="text-center py-24">
            <p className="text-slate-500 mb-4">We couldn't load your portal. Your session may have expired.</p>
            <button onClick={handleLogout} data-testid="dealer-relogin-btn"
              className="px-4 py-2 rounded-lg bg-[#0F2C59] text-white text-sm font-medium hover:bg-[#00529B]">
              Sign in again
            </button>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div className="relative rounded-3xl p-6 sm:p-8 text-white mb-6 overflow-hidden fade-up"
              style={{ background: "linear-gradient(135deg, #0F2C59 0%, #003366 50%, #00529B 100%)" }}
              data-testid="dealer-hero">
              <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
                <div>
                  <div className="text-cyan-200/80 text-sm font-medium">{t("welcomeBack")}</div>
                  <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold mt-1 tracking-tight">{data.profile.name}</h1>
                  <div className="flex items-center gap-3 mt-3 flex-wrap text-sm">
                    <span className="font-mono bg-white/10 px-2.5 py-1 rounded-md">{data.profile.code}</span>
                    <Pill tone={data.profile.status}>{data.profile.status}</Pill>
                    <span className="flex items-center gap-1.5 text-cyan-100/90">
                      <MapPin size={14} /> {data.profile.opco} · {data.profile.region} · {data.profile.rso} · {data.profile.ao}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="bg-white/10 backdrop-blur rounded-2xl px-5 py-3 border border-white/10">
                    <div className="text-[11px] uppercase tracking-wider text-cyan-200/80 font-semibold">{t("kpiAccrued")}</div>
                    <div className="font-heading text-2xl sm:text-3xl font-black mt-1">{inr(data.kpis.accrued)}</div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-24 -right-16 w-80 h-80 rounded-full bg-[#F59E0B]/25 blur-3xl" />
              <div className="absolute -top-20 -left-10 w-64 h-64 rounded-full bg-[#00A3E0]/25 blur-3xl" />
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard testid="dealer-kpi-accrued" label={t("kpiAccrued")} value={inr(data.kpis.accrued)} icon={Wallet} tone="navy" sub={t("kpiAccruedSub")} />
              <StatCard testid="dealer-kpi-settled" label={t("kpiSettled")} value={inr(data.kpis.settled)} icon={Banknote} tone="emerald" sub={t("kpiSettledSub")} />
              <StatCard testid="dealer-kpi-volume" label={t("kpiVolume")} value={`${num(data.kpis.volume_mt)} MT`} icon={Boxes} tone="gold" />
              <StatCard testid="dealer-kpi-schemes" label={t("kpiSchemes")} value={num(data.kpis.active_schemes)} icon={FileStack} tone="violet" />
            </div>

            {/* Tab switcher */}
            <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-200/70 mb-6" data-testid="dealer-tabs">
              <button onClick={() => setTab("playground")} data-testid="dealer-tab-playground"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "playground" ? "bg-white text-[#0F2C59] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                <Sparkles size={16} /> {t("tabPlayground")}
              </button>
              <button onClick={() => setTab("overview")} data-testid="dealer-tab-overview"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "overview" ? "bg-white text-[#0F2C59] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                <LayoutDashboard size={16} /> {t("tabActivity")}
              </button>
            </div>

            {tab === "playground" ? <DealerPlayground /> : (
            <>
              {/* Volume trend */}
              <Panel testid="dealer-volume-trend" title={t("volumeTitle")} subtitle={t("volumeSub")} className="mb-6">
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={data.volume_trend} margin={{ left: -10, right: 10 }}>
                    <defs>
                      <linearGradient id="dv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00529B" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#00529B" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip formatter={(v) => `${num(v)} MT`} />
                    <Area type="monotone" dataKey="volume" stroke="#00529B" strokeWidth={2.5} fill="url(#dv)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Panel>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Panel testid="dealer-schemes-panel" title={t("schemesTitle")} subtitle={t("schemesSub")}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                          <th className="py-2 pr-3">{t("colScheme")}</th><th className="py-2 pr-3">{t("colEngine")}</th>
                          <th className="py-2 pr-3 text-right">{t("colVolume")}</th><th className="py-2 text-right">{t("colIncentive")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.schemes.length ? data.schemes.map((s, i) => (
                          <tr key={i} className="drill-row border-b border-slate-50" data-testid={`dealer-scheme-${i}`}>
                            <td className="py-2.5 pr-3 font-mono text-xs text-slate-700">{s.scheme_code}</td>
                            <td className="py-2.5 pr-3"><Pill>{s.engine}</Pill></td>
                            <td className="py-2.5 pr-3 text-right text-slate-600">{num(s.qty)} MT</td>
                            <td className="py-2.5 text-right font-semibold text-[#00529B]">{inr(s.amount)}</td>
                          </tr>
                        )) : <tr><td colSpan={4} className="py-8 text-center text-slate-400">{t("noSchemes")}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </Panel>

                <Panel testid="dealer-settlements-panel" title={t("settlementsTitle")} subtitle={t("settlementsSub")}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                          <th className="py-2 pr-3">{t("colCreditNote")}</th><th className="py-2 pr-3 text-right">{t("colNet")}</th>
                          <th className="py-2 pr-3">{t("colMode")}</th><th className="py-2 text-right">{t("colStatus")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.settlements.length ? data.settlements.map((s, i) => (
                          <tr key={i} className="drill-row border-b border-slate-50" data-testid={`dealer-settlement-${i}`}>
                            <td className="py-2.5 pr-3 font-mono text-xs text-slate-500">{s.credit_note_no}</td>
                            <td className="py-2.5 pr-3 text-right font-semibold text-[#00529B]">{inr(s.net)}</td>
                            <td className="py-2.5 pr-3 text-slate-500 text-xs">{s.mode}</td>
                            <td className="py-2.5 text-right"><Pill tone={s.status}>{s.status}</Pill></td>
                          </tr>
                        )) : <tr><td colSpan={4} className="py-8 text-center text-slate-400">{t("noSettlements")}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              </div>

              <Panel testid="dealer-invoices-panel" title={t("invoicesTitle")} subtitle={t("invoicesSub")} className="mt-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                        <th className="py-2 pr-3">{t("colInvoice")}</th><th className="py-2 pr-3">{t("colDate")}</th>
                        <th className="py-2 pr-3 text-right">{t("colQty")}</th><th className="py-2 text-right">{t("colAmount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_invoices.map((i, idx) => (
                        <tr key={idx} className="drill-row border-b border-slate-50">
                          <td className="py-2.5 pr-3 font-mono text-xs text-slate-600">{i.invoice_no}</td>
                          <td className="py-2.5 pr-3 text-slate-500">{i.txn_date}</td>
                          <td className="py-2.5 pr-3 text-right text-slate-600">{num(i.qty_mt)}</td>
                          <td className="py-2.5 text-right text-slate-700">{inr(i.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
            )}
          </>
        )}
        <footer className="text-center text-[11px] text-slate-400 py-6 mt-4">
          SchemeCentral Dealer Portal · Adani Cement (ACC + ACL)
        </footer>
      </main>
    </div>
  );
}
